import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { useTenant } from '../../contexts/TenantContext';
import { useErrorToast } from '../../components/common';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { fetchAiModels, setAiModelActive } from '../../services/aiGovernanceService';
import { formatCost } from '../../constants/aiUsage';
import AiModelFormDialog from './AiModelFormDialog';
import AiModelPricingHistoryDialog from './AiModelPricingHistoryDialog';

/**
 * AI Models & Pricing — the JLL Super Admin registry.
 *
 * These prices are what a client is charged once its token allowance runs out,
 * so this page is the commercial counterpart to the Clients module's limits.
 */
const AiModelsPage = () => {
  const api = useApi();
  const { isJLLUser, hasRole } = useTenant();
  const { showErrorToast } = useErrorToast();

  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingCode, setTogglingCode] = useState(null);
  const [formDialog, setFormDialog] = useState({ open: false, model: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, model: null });

  const canManage = isJLLUser() && hasRole('JLL Super Admin');

  // ProtectedRoute's requireJLLUser also admits JLL Analyst, so explain the
  // refusal rather than rendering a blank page (same pattern as ClientsPage).
  useEffect(() => {
    if (!canManage) {
      showErrorToast('Access denied. Only JLL Super Admin can manage AI models and pricing.');
    }
  }, [canManage, showErrorToast]);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      setModels(await fetchAiModels(api, { includeInactive: true }));
    } catch (err) {
      console.error('Error loading AI models:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to load AI models'));
    } finally {
      setLoading(false);
    }
  }, [api, showErrorToast]);

  useEffect(() => {
    if (canManage) {
      loadModels();
    }
  }, [canManage, loadModels]);

  const handleToggleActive = async (model) => {
    setTogglingCode(model.modelCode);
    try {
      await setAiModelActive(api, model.modelCode, !model.isActive);
      await loadModels();
    } catch (err) {
      console.error('Error changing AI model status:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to change model status'));
    } finally {
      setTogglingCode(null);
    }
  };

  const handleSaved = () => {
    setFormDialog({ open: false, model: null });
    loadModels();
  };

  if (!canManage) {
    return null;
  }

  const unpricedCount = models.filter((model) => !model.isPriced).length;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: 'calc(100vh - 80px)',
        p: { xs: 2, sm: 2.5, md: 3 },
        margin: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
          >
            AI Models &amp; Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Rates used to charge clients for AI usage beyond their token allowance.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setFormDialog({ open: true, model: null })}
          sx={{
            backgroundColor: '#000000',
            '&:hover': { backgroundColor: '#333333' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Add Model
        </Button>
      </Box>

      {unpricedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unpricedCount} model{unpricedCount > 1 ? 's have' : ' has'} no price set. Usage on
          {unpricedCount > 1 ? ' those models' : ' that model'} is recorded but charged at zero until
          a price is added.
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={1}
        sx={{ borderRadius: '8px', overflow: 'auto', maxWidth: '100%' }}
      >
        <Table sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
              <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                Provider
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Input / 1M
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Output / 1M
              </TableCell>
              <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }}>
                Pricing
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Available
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : models.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No AI models registered yet. Add the models your AI gateway is configured with.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              models.map((model) => (
                <TableRow key={model.modelCode} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {model.modelName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {model.modelCode}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {model.provider || '—'}
                  </TableCell>

                  <TableCell align="right">
                    {model.isPriced ? (
                      formatCost(model.inputPricePerMillion, model.currency)
                    ) : (
                      <Chip label="Not priced" size="small" color="warning" variant="outlined" />
                    )}
                  </TableCell>

                  <TableCell align="right">
                    {model.isPriced ? formatCost(model.outputPricePerMillion, model.currency) : '—'}
                  </TableCell>

                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setHistoryDialog({ open: true, model })}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      <HistoryIcon fontSize="small" />
                      History
                    </Link>
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip
                      arrow
                      title={
                        model.isActive
                          ? 'Available in model pickers'
                          : 'Hidden from users; existing usage history is kept'
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={!!model.isActive}
                          disabled={togglingCode === model.modelCode}
                          onChange={() => handleToggleActive(model)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="right">
                    <IconButton
                      onClick={() => setFormDialog({ open: true, model })}
                      size="small"
                      color="primary"
                      title="Edit model and pricing"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AiModelFormDialog
        open={formDialog.open}
        model={formDialog.model}
        onClose={() => setFormDialog({ open: false, model: null })}
        onSaved={handleSaved}
      />

      <AiModelPricingHistoryDialog
        open={historyDialog.open}
        model={historyDialog.model}
        onClose={() => setHistoryDialog({ open: false, model: null })}
      />
    </Box>
  );
};

export default AiModelsPage;
